// Prospect service with API contracts matching backend schemas
// Types now centralized in src/types/prospect.ts
import { http } from './api';
import { PAGINATION } from '@/config/constants';
import type {
  Prospect,
  ProspectDetail,
  ProspectCreate,
  ProspectUpdate,
  ProspectFilters,
  ProspectsResponse,
  BulkStageUpdate,
  Activity,
  Note
} from '@/types/prospect';

// Re-export for backward compatibility (will be removed in next phase)
export type FilterOptions = ProspectFilters;
export type {
  Prospect,
  ProspectDetail,
  ProspectCreate,
  ProspectUpdate,
  Activity,
  Note,
  ProspectsResponse,
  BulkStageUpdate
};

export interface ImportJob {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_rows?: number;
  processed_rows?: number;
  errors?: string[];
}

class ProspectService {
  private baseUrl = '/prospects/';
  private campaignsUrl = '/campaigns';

  async getProspects(
    page = 1,
    pageSize: number = PAGINATION.PROSPECT_PAGE_SIZE,
    filters: FilterOptions = {},
    sortBy = 'createdAt',
    sortDir: 'asc' | 'desc' = 'desc'
  ): Promise<ProspectsResponse> {
    // Serialize array filters to comma-separated strings for query params
    const query: Record<string, string | number | boolean | undefined> = {
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
    };

    if (filters.search) query.search = filters.search;
    if (filters.industry?.length) query.industry = filters.industry.join(',');
    if (filters.companySize?.length) query.company_size = filters.companySize.join(',');
    if (filters.status?.length) query.status = filters.status.join(',');
    if (filters.location?.length) query.location = filters.location.join(',');
    if (filters.tags?.length) query.tags = filters.tags.join(',');
    if (filters.leadScore) {
      query.lead_score_min = filters.leadScore[0];
      query.lead_score_max = filters.leadScore[1];
    }

    return http<ProspectsResponse>('/prospects', {
      method: 'GET',
      query,
    });
  }

  async createProspect(data: ProspectCreate): Promise<Prospect> {
    const res = await http<{ success: boolean; data: Prospect }>('/prospects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  }

  async getProspect(id: string): Promise<ProspectDetail> {
    return http<ProspectDetail>(`/prospects/${id}`);
  }

  async updateProspect(id: string, updates: Partial<Prospect>): Promise<Prospect> {
    return http<Prospect>(`/prospects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async bulkUpdateStage(update: BulkStageUpdate): Promise<{ updated: number }> {
    return http<{ updated: number }>(`/prospects/bulk-stage`, {
      method: 'POST',
      body: JSON.stringify(update)
    });
  }

  async importProspects(file: File): Promise<ImportJob> {
    const formData = new FormData();
    formData.append('file', file);
    
    return http<ImportJob>(`/prospects/import`, {
      method: 'POST',
      body: formData
    });
  }

  async getImportStatus(jobId: string): Promise<ImportJob> {
    return http<ImportJob>(`/prospects/import/${jobId}`);
  }

  // Campaign helpers
  async getCampaigns(): Promise<unknown[]> {
    return http<unknown[]>(`/campaigns`);
  }

  async updateProspectStatus(prospectId: string, status: string): Promise<void> {
    return http<void>(`/prospects/${prospectId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  async getProspectContacts(prospectId: string): Promise<Activity[]> {
    const data = await http<{ data: Array<{ id: string; type: string; subject?: string; message?: string; sent_at?: string; created_at?: string; status?: string; response?: string; website_url?: string }> }>(`/prospects/${prospectId}/contacts`);
    
    return data.data.map((contact) => ({
      id: contact.id,
      type: contact.type === 'email' ? 'email' as const : 
            contact.type === 'contact_form' ? 'contact_form' as const : 
            'note' as const,
      description: contact.subject || contact.message || `${contact.type} contact`,
      timestamp: contact.sent_at || contact.created_at || new Date().toISOString(),
      userId: 'system',
      metadata: {
        status: contact.status,
        response: contact.response,
        website_url: contact.website_url,
      }
    }));
  }

  async addNote(prospectId: string, noteContent: string): Promise<Prospect> {
    const prospect = await this.getProspect(prospectId);
    const existingNotes = prospect.notes || '';
    const timestamp = new Date().toISOString();
    const newNote = `[${timestamp}] ${noteContent}`;
    const updatedNotes = existingNotes 
      ? `${existingNotes}\n\n${newNote}` 
      : newNote;
    
    return this.updateProspect(prospectId, { notes: updatedNotes });
  }
}

export const prospectService = new ProspectService();